import { getMessage } from "../data/locales";
import { extractTopicTestHistory } from "./extractTopicTestHistory";

/**
 * Builds the pre-test modal payload from in-memory history (no network).
 * @param {{
 *   tema: { nombre: string, notaMinima?: number, tiempoPorPregunta?: number },
 *   bloqueNombre: string,
 *   userHistory: Array,
 *   courseId: string|number|null|undefined
 * }} params
 */
export function buildPreTestInfoFromMemory({ tema, bloqueNombre, userHistory, courseId }) {
  const { historial, intentos } = extractTopicTestHistory(
    userHistory,
    courseId,
    bloqueNombre,
    tema.nombre,
  );
  return {
    bloque: bloqueNombre,
    temaObj: {
      ...tema,
      notaMinima: tema.notaMinima ?? 5.0,
      tiempoPorPregunta: tema.tiempoPorPregunta ?? 50,
    },
    historial,
    intentos,
  };
}

/**
 * Whether opening the pre-test sheet should unmount the selection UI.
 * Kept as an explicit contract so regressions cannot reintroduce full-page flashes.
 * @returns {false}
 */
export function shouldUnmountSelectionForPreTest() {
  return false;
}

/**
 * Label shown on the primary start button while the question bank loads.
 * @param {boolean} isStarting
 * @returns {string}
 */
export function resolveStartEvaluationLabel(isStarting) {
  return isStarting
    ? getMessage("tests.startingEvaluation")
    : getMessage("tests.startEvaluation");
}

const QUESTION_BANK_LOAD_ERROR = "Error al cargar las preguntas.";

/**
 * Loads the question bank from the server. A previous successful response must not
 * be reused: access can be revoked after the first launch.
 *
 * @param {string} url
 * @param {AbortSignal} [signal]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function loadQuestionBank(url, signal) {
  const response = await fetch(url, { credentials: "include", signal });
  if (!response.ok) {
    let body = {};
    try {
      const parsed = await response.json();
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        body = parsed;
      }
    } catch {
      body = {};
    }
    const message = typeof body.error === "string" && body.error.trim()
      ? body.error.trim()
      : QUESTION_BANK_LOAD_ERROR;
    const error = new Error(message);
    if (body.accesoTests) {
      error.accesoTests = body.accesoTests;
    }
    throw error;
  }
  const data = await response.json();
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error(QUESTION_BANK_LOAD_ERROR);
  }
  return data;
}

/**
 * A history refresh is stale once a newer refresh or a saved attempt has moved
 * the generation forward.
 *
 * @param {number} requestGeneration
 * @param {number} currentGeneration
 * @returns {boolean}
 */
export function shouldApplyHistoryRefresh(requestGeneration, currentGeneration) {
  return requestGeneration === currentGeneration;
}

/**
 * @param {object} error
 * @returns {boolean}
 */
export function isRevokedTestAccess(error) {
  return Boolean(
    error
    && typeof error === "object"
    && "accesoTests" in error
    && error.accesoTests
    && error.accesoTests.allowed === false,
  );
}

/**
 * Access flags from a denied bank response often omit the reason.
 * The server sends that text as the error message, and the banner must keep it.
 *
 * @param {Error & { accesoTests: { allowed: boolean, message?: string } }} error
 * @returns {{ allowed: false, message?: string }}
 */
export function accessStateFromRevokedBankError(error) {
  const source = error.accesoTests;
  const access = {
    ...source,
    allowed: false,
  };
  const stated = typeof source.message === "string" ? source.message.trim() : "";
  const denied = typeof error.message === "string" ? error.message.trim() : "";
  const message = stated || denied;
  if (message) {
    access.message = message;
  }
  return access;
}
